// Tirage des briques de « La Mine aux briques » : logique PURE (rareté + choix du loot + pity +
// doublons). Le hasard est INJECTÉ (`rng: () => number` dans [0,1)) : jamais de Math.random ni de
// Date.now DANS ce module. En prod, le SERVEUR passe Math.random (server/mine.ts) ; en test, un
// générateur seedé. Hors simulation : rien ici n'entre dans le hash d'état.

import type { Rarity } from './economy'
import { LOOTS, type Loot } from './loots'

// Taux de base par rareté (somme = 1.00). Sert d'affichage/référence ; le roll de drawRarity
// utilise les seuils cumulés correspondants (0.70 / 0.92 / 0.99).
export const RARITY_RATE: Record<Rarity, number> = { courant: 0.70, rare: 0.22, epique: 0.07, mega: 0.01 }
// Pity : après PITY_EPIC tirages consécutifs sans épique+ → épique garanti ; PITY_MEGA sans méga → méga garanti.
export const PITY_EPIC = 10
export const PITY_MEGA = 40
// Éclats gagnés quand un cosmétique tiré est déjà possédé (doublon), par rareté.
export const SHARD_ON_DUP: Record<Rarity, number> = { courant: 10, rare: 25, epique: 60, mega: 150 }
// Coût en éclats pour acheter directement un cosmétique à la boutique, par rareté.
export const SHARD_COST: Record<Rarity, number> = { courant: 50, rare: 150, epique: 400, mega: 1200 }

export interface Pity { epic: number; mega: number }  // tirages consécutifs sans épique+/méga

// Ordre des raretés, du plus commun au plus rare. Sert au PLANCHER de profondeur (26/07).
const RARITY_ORDER: readonly Rarity[] = ['courant', 'rare', 'epique', 'mega']

/**
 * Remonte `rarity` à `floor` si elle est en dessous. Ne redescend JAMAIS rien : un méga tiré sous
 * un plancher épique reste un méga. `floor` null (la surface) laisse tout passer tel quel.
 * PURE — c'est la règle « en bas, on ne trouve que du beau » réduite à sa plus simple expression.
 */
export function raiseToFloor(rarity: Rarity, floor: Rarity | null): Rarity {
  if (!floor) return rarity
  return RARITY_ORDER.indexOf(rarity) < RARITY_ORDER.indexOf(floor) ? floor : rarity
}

// Tire une rareté. Le PITY passe AVANT le roll : méga garanti d'abord (priorité sur épique quand
// les deux seuils tombent le même tirage), puis épique garanti, sinon roll sur les seuils cumulés
// 0.70 / 0.92 / 0.99. Le PLANCHER de profondeur (`floor`, 26/07) s'applique ENSUITE : il ne peut
// que remonter le résultat, donc il ne peut jamais affaiblir une garantie du pity.
// Le nouveau pity se calcule sur la rareté FINALE, plancher compris — un épique offert par le fond
// remet donc bien la jauge épique à zéro, et la jauge ne ment pas au joueur.
export function drawRarity(rng: () => number, pity: Pity, floor: Rarity | null = null): { rarity: Rarity; pity: Pity } {
  let rarity: Rarity
  if (pity.mega + 1 >= PITY_MEGA) rarity = 'mega'
  else if (pity.epic + 1 >= PITY_EPIC) rarity = 'epique'
  else {
    const r = rng()
    if (r < 0.70) rarity = 'courant'
    else if (r < 0.92) rarity = 'rare'
    else if (r < 0.99) rarity = 'epique'
    else rarity = 'mega'
  }
  rarity = raiseToFloor(rarity, floor)
  const isEpicPlus = rarity === 'epique' || rarity === 'mega'
  const isMega = rarity === 'mega'
  return {
    rarity,
    pity: {
      epic: isEpicPlus ? 0 : pity.epic + 1,
      mega: isMega ? 0 : pity.mega + 1,
    },
  }
}

// Part de MÉGA dans une BRIQUE D'OR (le reste est épique). La brique d'or est la récompense du 7e
// jour d'une série (shared/economy dailyReward) : elle ne peut PAS retomber sur du courant/rare.
export const GOLDEN_MEGA_RATE = 0.10

/**
 * Tirage d'une BRIQUE D'OR (série de 7 jours) : épique garanti, méga dans GOLDEN_MEGA_RATE des cas.
 * Le pity avance EXACTEMENT comme pour un tirage normal (une brique d'or « paie » le compteur
 * épique, et le compteur méga seulement si elle sort méga) : impossible d'en tirer un avantage caché
 * ni de casser la garantie dure des briques ordinaires.
 */
export function drawRarityGolden(rng: () => number, pity: Pity): { rarity: Rarity; pity: Pity } {
  const rarity: Rarity = rng() < GOLDEN_MEGA_RATE ? 'mega' : 'epique'
  return { rarity, pity: { epic: 0, mega: rarity === 'mega' ? 0 : pity.mega + 1 } }
}

// Choisit un loot dans la rareté tirée. Les « frais » = les non-cosmétiques (sacs de pièces,
// consommables : toujours disponibles) ET les cosmétiques non encore possédés. S'il reste du frais
// → on le tire (duplicate:false). Sinon (tous les cosmétiques de la rareté déjà possédés) → doublon
// dans le pool complet (duplicate:true → convertible en éclats côté serveur). L'index est toujours
// valide car rng() ∈ [0,1) ⇒ floor(rng()*len) ∈ [0, len).
export function pickLoot(rng: () => number, rarity: Rarity, owned: Set<string>): { loot: Loot; duplicate: boolean } {
  const pool = LOOTS.filter((l) => l.rarity === rarity)
  const fresh = pool.filter((l) => l.type !== 'cosmetic' || !owned.has(l.id))
  if (fresh.length > 0) {
    return { loot: fresh[Math.floor(rng() * fresh.length)]!, duplicate: false }
  }
  return { loot: pool[Math.floor(rng() * pool.length)]!, duplicate: true }
}
